import { useTranslation } from 'react-i18next';
import { Button, Flex } from '@kognitos/lattice';

interface CursorPaginationProps {
  currentPage: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  /** Total number of pages for the full result set. */
  totalPages?: number;
  /** When true and totalPages is omitted, show "Page N" instead of estimating "of Y". */
  suppressEstimatedTotal?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
  className?: string;
}

export const CursorPagination = ({
  currentPage,
  canGoNext,
  canGoPrev,
  totalPages,
  suppressEstimatedTotal = false,
  isLoading = false,
  isFetchingNextPage = false,
  onNextPage,
  onPrevPage,
  className = '',
}: CursorPaginationProps) => {
  const { t: tCommon } = useTranslation('common');

  const hasExactTotal = totalPages !== undefined;
  const resolvedTotal = hasExactTotal
    ? Math.max(totalPages, currentPage + 1)
    : currentPage + 1 + (canGoNext ? 1 : 0);
  const pageLabel =
    hasExactTotal || !suppressEstimatedTotal
      ? tCommon('common.pagination.pageOf', {
          current: currentPage + 1,
          total: Math.max(resolvedTotal, currentPage + 1),
        })
      : `${tCommon('common.pagination.page')} ${currentPage + 1}`;

  return (
    <Flex justify="between" align="center" width="full" className={className}>
      <span className="text-sm text-muted-foreground">{pageLabel}</span>
      <Flex gap={2}>
        <Button
          size="default"
          variant="outline"
          onClick={onPrevPage}
          disabled={!canGoPrev || isLoading}
        >
          {tCommon('common.pagination.previous')}
        </Button>

        <Button
          size="default"
          variant="outline"
          onClick={onNextPage}
          disabled={!canGoNext || isLoading || isFetchingNextPage}
          isLoading={isFetchingNextPage}
        >
          {tCommon('common.pagination.next')}
        </Button>
      </Flex>
    </Flex>
  );
};
